"use client";

import * as React from "react";
import CurrencyInput, { type CurrencyInputProps } from "react-currency-input-field";

import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * NKCurrencyInput
 *
 * Locale-aware currency field. CAD by default, USD opt-in for U.S.-listed
 * holdings. Wraps `react-currency-input-field` for paste-from-formatted-
 * string handling, locale grouping, and safe parsing.
 *
 * Used heavily in transaction entry: every HELOC draw, dividend, ROC,
 * interest payment, capitalized-interest event. Worth getting right.
 *
 * Behaviour:
 * - tabular-nums always (financial alignment)
 * - right-align option for table cells
 * - shows empty (not "$0.00") on focus when value is 0 so users can type
 *   without first deleting a zero
 * - on blur, commits formatted display
 * - paste handles "$1,234.56" / "1234.56" / "1,234.56" cleanly
 *
 * Per the no-assumed-values doctrine ([`docs/FINANCE_RULES.md`]), this
 * component does NOT supply a default amount. Callers must pass `value`
 * explicitly. Empty state means "the user hasn't entered it yet" — not
 * "assume zero."
 * ────────────────────────────────────────────────────────────────────────── */

export interface NKCurrencyInputProps extends Omit<
  CurrencyInputProps,
  "intlConfig" | "value" | "onValueChange" | "onChange" | "ref"
> {
  /** Current numeric value in major units (e.g. 1234.56 for $1,234.56). */
  value: number | "" | null | undefined;
  /** Called with the new numeric value (or `undefined` if the field is empty). */
  onChange: (value: number | undefined) => void;
  /** Currency code. Defaults to CAD. */
  currency?: "CAD" | "USD";
  /** Locale. Defaults to `en-CA`. */
  locale?: "en-CA" | "en-US";
  /** Right-align for table cells. Defaults to left. */
  align?: "left" | "right";
  /** Visual error state (e.g. failed validation). */
  error?: boolean;
}

export const NKCurrencyInput = React.forwardRef<HTMLInputElement, NKCurrencyInputProps>(
  (
    {
      className,
      value,
      onChange,
      currency = "CAD",
      locale = "en-CA",
      align = "left",
      error,
      disabled,
      placeholder,
      ...rest
    },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);

    // When focused and value is 0, display empty so users can type without
    // first deleting a leading zero.
    const displayValue =
      focused && value === 0
        ? ""
        : value === null || value === undefined || value === ""
          ? ""
          : value;

    return (
      <CurrencyInput
        ref={ref}
        intlConfig={{ locale, currency }}
        decimalsLimit={2}
        decimalScale={focused ? undefined : 2}
        allowNegativeValue={false}
        value={displayValue}
        disabled={disabled}
        placeholder={placeholder ?? "$0.00"}
        onValueChange={(_value, _name, values) => {
          if (values?.float == null || Number.isNaN(values.float)) {
            onChange(undefined);
          } else {
            onChange(values.float);
          }
        }}
        onFocus={(e) => {
          setFocused(true);
          rest.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          rest.onBlur?.(e);
        }}
        className={cn(
          // base — match shadcn Input shape
          "border-input bg-background flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none",
          "tabular-nums",
          "placeholder:text-muted-foreground",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          align === "right" && "text-right",
          error &&
            "border-destructive ring-destructive/20 focus-visible:border-destructive focus-visible:ring-destructive/40",
          className,
        )}
        {...rest}
      />
    );
  },
);
NKCurrencyInput.displayName = "NKCurrencyInput";
